import React, { useState, useEffect } from 'react';
import { 
  CodeBracketIcon, 
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { Card } from '../ui/card';
import { useAuth } from '../../contexts/AuthContext';

const challenges = [
    { 
      title: 'Missing Keyword', 
      code: '______ berechneSumme(a, b) { return a + b; }', 
      options: ['function', 'class', 'let'], 
      correct: 'function',
      explanation: 'Folja "berechnen" tregon një veprim që zakonisht kryhet nga një funksion.' 
    },
    { 
      title: 'Variable Declaration', 
      code: '______ nutzerName = "Fatli";', 
      options: ['const', 'import', 'export'], 
      correct: 'const',
      explanation: 'Për të deklaruar një emër (Der Name) që nuk dryshon përdorim const.' 
    },
    { 
      title: 'Logical Check', 
      code: 'if (istVerbunden ______ true) { ... }', 
      options: ['===', '=>', '::'], 
      correct: '===',
      explanation: '"istVerbunden" (është i lidhur) krahasohet duke përdorur ===.' 
    },
    { 
      title: 'Array Translation', 
      code: 'const liste = ["Eins", "Zwei", "______"];', 
      options: ['Drei', 'Frei', 'Brei'], 
      correct: 'Drei',
      explanation: 'Vazhdimi i numërimit pas Eins, Zwei është Drei (Tre).' 
    },
    { 
      title: 'Async Operation', 
      code: '______ function ladeDaten() { ... }', 
      options: ['async', 'await', 'then'], 
      correct: 'async',
      explanation: 'Përdorim "async" për të deklaruar një funksion asinkron për ngarkimin e të dhënave (lade Daten).' 
    },
    { 
      title: 'Class Definition', 
      code: '______ Datenbank { constructor() { ... } }', 
      options: ['class', 'function', 'object'], 
      correct: 'class',
      explanation: '"Datenbank" (Baza e të dhënave) përfaqësohet si një klasë në këtë strukturë.' 
    },
    { 
      title: 'Encryption Term', 
      code: 'const geheim = ______("MeinPasswort");', 
      options: ['verschlüsseln', 'löschen', 'zeigen'], 
      correct: 'verschlüsseln',
      explanation: 'Për të siguruar fjalëkalimin, ne duhet ta enkriptojmë (verschlüsseln) atë.' 
    },
    { 
      title: 'Loop Grammar', 
      code: 'for (let i = 0; i < ______; i++) { ... }', 
      options: ['limit', 'mit', 'bit'], 
      correct: 'limit',
      explanation: '"Limit" është kufiri i përsëritjes së ciklit.' 
    },
    { 
      title: 'User Input', 
      code: 'const eingabe = prompt("Wie ist ______ Name?");', 
      options: ['dein', 'deine', 'deines'], 
      correct: 'dein',
      explanation: 'Meqenëse "Name" është gjinia mashkullore (Der Name), përdorim "dein".' 
    },
    {
      title: 'Module Import',
      code: '______ { useState } from "react";',
      options: ['import', 'export', 'require'],
      correct: 'import',
      explanation: 'Përdorim fjalën kyçe "import" për të marrë (importuar) funksione ose komponentë nga libraritë.'
    },
    {
      title: 'Object Destructuring',
      code: 'const { benutzerName, istAktiv } = ______;',
      options: ['nutzer', 'import', 'let'],
      correct: 'nutzer',
      explanation: 'Destrukturimi i objektit "nutzer" na lejon të marrim direkt vetitë e tij.'
    },
    {
      title: 'Function Return',
      code: 'function addiere(a, b) { ______ a + b; }',
      options: ['return', 'break', 'export'],
      correct: 'return',
      explanation: 'Përdorim "return" (kthe) për të kthyer vlerën e llogaritur nga funksioni addiere.'
    }
];

const ProgrammerSyntaxGame = () => {
  const [shuffled, setShuffled] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [score, setScore] = useState(0);
  const [finished, setFinished] = useState(false);
  const { user, updateUserStats } = useAuth();

  useEffect(() => {
    setShuffled([...challenges].sort(() => Math.random() - 0.5));
  }, []);

  const handleSelect = (opt) => {
    if (selected) return;
    setSelected(opt);
    if (opt === shuffled[currentIdx].correct) {
      setScore(prev => prev + 20);
    }
    
    setTimeout(async () => {
      if (currentIdx < shuffled.length - 1) {
        setCurrentIdx(prev => prev + 1);
        setSelected(null);
      } else {
        const finalScore = score + (opt === shuffled[currentIdx].correct ? 20 : 0);
        const totalXp = (user?.xp || 0) + finalScore;
        await updateUserStats({ xp: totalXp });
        setFinished(true);
      }
    }, 1500);
  };

  if (shuffled.length === 0) return null;

  if (finished) {
    return (
      <Card className="bg-zinc-950 text-white border-zinc-800 p-12 text-center border border-white/10 shadow-2xl">
        <SparklesIcon className="w-16 h-16 text-yellow-400 mx-auto mb-6" />
        <h2 className="text-2xl font-bold text-white mb-2">Sintaksa u korrigjua!</h2>
        <p className="text-brand-text/50 mb-8">Keni fituar {score} XP për saktësinë e kodit.</p>
        <button onClick={() => window.location.reload()} className="px-10 py-4 bg-white text-black font-black rounded-2xl">Luaj përsëri</button>
      </Card>
    );
  }

  const current = shuffled[currentIdx];

  return (
    <div className="bg-[#0b0e14] rounded-[3rem] overflow-hidden border border-white/5 font-mono">
      <div className="p-6 border-b border-white/5 bg-black/40 flex items-center gap-3">
         <CodeBracketIcon className="w-5 h-5 text-yellow-400" />
         <span className="text-xs text-yellow-400 font-bold tracking-widest uppercase">{current.title}</span>
      </div>

      <div className="p-10 text-center">
        <div className="bg-black/50 p-10 rounded-3xl border border-white/5 mb-10 text-xl md:text-2xl text-white leading-relaxed">
           <code>{current.code.split('______').map((part, i, arr) => (
             <React.Fragment key={i}>
               {part}
               {i < arr.length - 1 && (
                 <span className={`mx-2 px-4 py-1 rounded-xl border-2 border-dashed ${selected ? (selected === current.correct ? 'border-green-500 text-green-500' : 'border-red-500 text-red-500') : 'border-yellow-500/30 text-brand-primary animate-pulse'}`}>
                   {selected || '????'}
                 </span>
               )}
             </React.Fragment>
           ))}</code>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {current.options.map((opt, i) => (
            <button
              key={i}
              onClick={() => handleSelect(opt)}
              disabled={selected !== null}
              className={`
                p-5 rounded-2xl border-2 font-bold transition-all
                ${selected === opt ? (opt === current.correct ? 'border-green-500 bg-green-500/10 text-green-500' : 'border-red-500 bg-red-500/10 text-red-500') : 
                  (selected && opt === current.correct ? 'border-green-500 bg-green-500/5 text-green-500' : 'border-white/5 text-white/40 hover:border-white/20 hover:text-white')}
              `}
            >
              {opt}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProgrammerSyntaxGame;
